import { IImageBySizes } from '../base.interface';
import { IBase } from '../base.interface';
import { IShop } from '../shop.interface';
import { ICatalogToPromotion } from './catalog-to-promotion.interface';
import { IConnector } from './connector.interface';
import { OrderMode } from '../order.interface';
import { IPack } from './pack.interface';
import { IProductVariation } from './product-variation.interface';
import { IProduct } from './product.interface';
import { ISchedule } from './schedule.interface';
import { ITag } from './tag.interface';

export enum PromotionScope {
  // La promotion est disponible dans le panier
  CART = 'cart',

  // La promotion est disponible dans le catalogue au niveau du site
  CATALOG = 'catalog',
}

export interface IPromotion extends IBase {
  name: string;
  code?: string;
  description: string;
  disabled: boolean;
  tags?: ITag[];
  tagsIds: string[];
  startDate?: any;
  endDate?: any;
  images?: IImageBySizes;
  orderModes: OrderMode[];

  connectors?: IConnector[];

  priority?: number;
  scopes?: PromotionScope[];

  conditions?: IPromotionCondition[];
  actions?: IPromotionAction[];

  maxUses?: number;
  cumulative?: boolean;
  repeatable?: boolean;
  uniqueByCustomer?: boolean;
  availabilitySchedule?: ISchedule;

  shopsIds?: string[];
  shops?: Pick<IShop, '_id' | 'internalName' | 'publicName'>[];
  catalogToPromotions: ICatalogToPromotion[];


  // OLD FIELDS
  type: PromotionTypes;
  discountMode?: PromotionDiscountModes;
  discountValue?: number;
  minPrice?: number;
  isPackExcluded?: boolean;
  usedCount?: number;
  usedCustomersIds?: string[];
  customerOnly: boolean;
  excludedTagsIds?: string[];
  customersIds?: string[];
  firstOrderOnly?: boolean;
  requiredPacks?: IPack[];
  requiredProductVariations?: IProductVariation[];
  requiredProductVariationsIds: string[];
  discountablePacks?: IPack[];
  discountableProductVariations?: IProductVariation[];
  discountableProductVariationsIds?: string[];
  neededCount?: number;
  offeredCount?: number;
  products: IProduct[];
  packs: IPack[];
}

export const isPromotionCode = <T extends Partial<IPromotion>>(promotion: T): boolean => {
  return typeof promotion.code !== 'undefined' && promotion.code !== null;
}

export enum PromotionTypes {
  LEAST_EXPENSIVE = 'LEAST_EXPENSIVE',
  DISCOUNT = 'DISCOUNT',
  GIVEAWAY = 'GIVEAWAY'
}

export enum PromotionDiscountModes {
  PERCENTAGE = 'PERCENTAGE',
  FIXED = 'FIXED'
}

////////////////////////////////////////////////////////////

export enum PromotionConditionOperator {
  GREATER_THAN = '$gt',
  GREATER_THAN_OR_EQUAL = '$gte',
  EQUAL = '$eq',
  NOT_EQUAL = '$ne',
  LESS_THAN = '$lt',
  LESS_THAN_OR_EQUAL = '$lte',
  IN = '$in',
  NOT_IN = '$notIn',
}

// Il faudrait probablement que packs et produtcs ne soient pas des arrays 
export enum PromotionConditionType {
  // 🎂 Uniquement pour les clients dont c'est l'anniversaire et dans la fenêtre de temps
  BIRTHDAY_WINDOW = 'birthday_window',

  // 💰 Montant minimum de la commande
  ORDER_TOTAL = 'order_total',

  // 📦 IDs des packs présents (ou absents) dans la commande
  PACKS = 'packs',

  // 🎯 IDs des variations de produits présentes (ou absentes)
  PRODUCT_VARIATIONS = 'product_variations',

  // 🏷️ Tags obligatoires dans la commande
  // TAGS = 'tags',

  // 👤 IDs des clients ciblés
  CUSTOMERS = 'customers',

  // 🆕 Combien de commandes a fait le client
  // CUSTOMER_ORDER_TOTAL = 'customer_order_total',
}

export type IPromotionConditionMinOrderTotal = {
  type: PromotionConditionType.ORDER_TOTAL;
  referenceId: ReferenceId;
  details: {
    operator: PromotionConditionOperator.GREATER_THAN_OR_EQUAL | PromotionConditionOperator.LESS_THAN_OR_EQUAL;
    value: number;
  }
}

export type IPromotionConditionBirthdayWindow = {
  type: PromotionConditionType.BIRTHDAY_WINDOW;
  referenceId: ReferenceId;
  details: {
    daysAfterBirthday: number;
    daysBeforeBirthday: number;
  }
}

export type IPromotionConditionPacks = {
  type: PromotionConditionType.PACKS;
  referenceId: ReferenceId;
  details: {
    operator: PromotionConditionOperator.IN | PromotionConditionOperator.NOT_IN;
    value: string[];
    quantity: number;
  }
}

export type IPromotionConditionProductVariations = {
  type: PromotionConditionType.PRODUCT_VARIATIONS;
  referenceId: ReferenceId;
  details: {
    operator: PromotionConditionOperator.IN | PromotionConditionOperator.NOT_IN;
    value: string[];
    quantity: number;
  }
}

// export type IPromotionConditionTags = {
//   type: PromotionConditionType.TAGS;
//   referenceId: ReferenceId;
//   details: {
//     operator: PromotionConditionOperator.IN | PromotionConditionOperator.NOT_IN;
//     value: string[];
//     quantity: number;
//   }
// }

export type IPromotionConditionCustomers = {
  type: PromotionConditionType.CUSTOMERS;
  referenceId: ReferenceId;
  details: {
    operator: PromotionConditionOperator.IN | PromotionConditionOperator.NOT_IN;
    value: string[];
  }
}

export type IPromotionCondition = IPromotionConditionMinOrderTotal
  | IPromotionConditionBirthdayWindow
  | IPromotionConditionPacks
  | IPromotionConditionProductVariations 
  // | IPromotionConditionTags
  | IPromotionConditionCustomers;

export enum PromotionActionType {
  ADD_PRODUCT_VARIATION = 'add_product_variation',
  ADD_PACK = 'add_pack',
  ORDER_DISCOUNT_FIXED = 'order_discount_fixed',
  ORDER_DISCOUNT_PERCENTAGE = 'order_discount_percentage',
  ITEMS_DISCOUNT_FIXED = 'items_discount_fixed',
  ITEMS_DISCOUNT_PERCENTAGE = 'items_discount_percentage',
}

export type ReferenceId = string;

export type IPromotionActionAddProductVariation = {
  type: PromotionActionType.ADD_PRODUCT_VARIATION;
  referenceId: ReferenceId;
  details: {
    value: string;
  }
}

export type IPromotionActionAddPack = {
  type: PromotionActionType.ADD_PACK;
  referenceId: ReferenceId;
  details: {
    value: string;
  }
}

export type IPromotionActionOrderDiscountFixed = {
  type: PromotionActionType.ORDER_DISCOUNT_FIXED;
  referenceId: ReferenceId;
  details: {
    value: number;
  }
}

export type IPromotionActionOrderDiscountPercentage = {
  type: PromotionActionType.ORDER_DISCOUNT_PERCENTAGE;
  referenceId: ReferenceId;
  details: {
    value: number;
  }
}

export enum PromotionItemDiscountStrategy {
  ALL = "ALL",
  CHEAPEST = "CHEAPEST",
  BEST_VALUE = "BEST_VALUE",
  MOST_EXPENSIVE = "MOST_EXPENSIVE",
}

export type IPromotionActionItemsDiscountFixed = {
  type: PromotionActionType.ITEMS_DISCOUNT_FIXED;
  referenceId: ReferenceId;
  details: {
    value: number;
    targets: ReferenceId[];
    strategy: PromotionItemDiscountStrategy;
  }
}

export type IPromotionActionItemsDiscountPercentage = {
  type: PromotionActionType.ITEMS_DISCOUNT_PERCENTAGE;
  referenceId: ReferenceId;
  details: {
    value: number;
    targets: ReferenceId[];
    strategy: PromotionItemDiscountStrategy;
  }
}

export type IPromotionAction = IPromotionActionAddProductVariation | IPromotionActionAddPack | IPromotionActionOrderDiscountFixed | IPromotionActionOrderDiscountPercentage | IPromotionActionItemsDiscountFixed | IPromotionActionItemsDiscountPercentage;
